import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SchemaOrg from '@/components/base/SchemaOrg';
import { brandInfo, testimonials } from '@/mocks/brand';

function BrandIntro() {
  const { t } = useTranslation();

  return (
    <section className="w-full mb-12 md:mb-16">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
        {t('about.brand.title')}
      </h2>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-10">
        <div>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-4">
            {brandInfo.description}
          </p>
          <div className="bg-background-100 rounded-lg p-5 mb-4">
            <h3 className="text-sm font-semibold text-foreground-800 mb-2">我们的使命</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{brandInfo.mission}</p>
          </div>
          <div className="bg-background-100 rounded-lg p-5">
            <h3 className="text-sm font-semibold text-foreground-800 mb-2">我们的愿景</h3>
            <p className="text-sm text-foreground-600 leading-relaxed">{brandInfo.vision}</p>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3 md:gap-4">
          {brandInfo.stats.map((stat) => (
            <div
              key={stat.label}
              className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5 text-center"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 mx-auto mb-2 md:mb-3 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500">
                <i className={`${stat.icon} text-lg md:text-xl`}></i>
              </div>
              <p className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-1">
                {stat.value}
              </p>
              <p className="text-xs md:text-sm text-foreground-500">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CoreValues() {
  return (
    <section className="w-full mb-12 md:mb-16 py-10 md:py-12 bg-background-100 rounded-lg px-6 md:px-10">
      <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
        核心价值观
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {brandInfo.values.map((value, index) => (
          <div key={index} className="bg-background-50 rounded-lg p-5 text-center">
            <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
              <span className="font-heading font-bold text-sm">{index + 1}</span>
            </div>
            <h3 className="text-sm font-semibold text-foreground-800 mb-2">{value.title}</h3>
            <p className="text-xs text-foreground-500 leading-relaxed">{value.description}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ReputationSection() {
  const { t } = useTranslation();

  return (
    <section className="w-full mb-12 md:mb-16">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
        {t('about.reputation.title')}
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
        {testimonials.map((item) => (
          <div
            key={item.id}
            className="bg-background-50 border border-background-200 rounded-lg p-4 md:p-5"
          >
            <div className="flex items-center gap-1 mb-3">
              {Array.from({ length: item.rating }).map((_, i) => (
                <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
              ))}
            </div>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              &ldquo;{item.content}&rdquo;
            </p>
            <div className="flex items-center gap-3">
              <img
                src={item.avatar}
                alt={item.name}
                className="w-10 h-10 rounded-full object-cover"
                loading="lazy"
              />
              <div>
                <p className="text-sm font-medium text-foreground-800">{item.name}</p>
                <p className="text-xs text-foreground-500">{item.role}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ContactSection() {
  const { t } = useTranslation();

  const contactItems = [
    { icon: 'ri-map-pin-line', label: t('about.contact.address'), value: brandInfo.contact.address },
    { icon: 'ri-phone-line', label: t('about.contact.phone'), value: brandInfo.contact.phone },
    { icon: 'ri-mail-line', label: t('about.contact.email'), value: brandInfo.contact.email },
    { icon: 'ri-wechat-line', label: t('about.contact.wechat'), value: brandInfo.contact.wechat },
  ];

  return (
    <section className="w-full mb-12 md:mb-16">
      <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-6">
        {t('about.contact.title')}
      </h2>
      <div className="bg-background-100 rounded-lg p-6 md:p-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
          {contactItems.map((item) => (
            <div key={item.label} className="flex items-start gap-3">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-500 flex-shrink-0">
                <i className={`${item.icon} text-lg`}></i>
              </div>
              <div>
                <p className="text-xs text-foreground-500 mb-0.5">{item.label}</p>
                <p className="text-sm font-medium text-foreground-800">{item.value}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-6 pt-5 border-t border-background-200">
          <p className="text-xs text-foreground-500">
            工作时间：{brandInfo.contact.businessHours}
          </p>
        </div>
      </div>
    </section>
  );
}

export default function About() {
  const { t } = useTranslation();

  const breadcrumbItems = [{ label: t('about.title') }];

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于豆花视频',
    description: brandInfo.description,
    url: 'https://www.douhuavideo.com/about',
    about: {
      '@type': 'Organization',
      name: '豆花视频',
      description: brandInfo.description,
      foundingDate: brandInfo.founded,
      address: {
        '@type': 'PostalAddress',
        streetAddress: brandInfo.contact.address,
        addressLocality: '北京',
        addressCountry: 'CN',
      },
      contactPoint: {
        '@type': 'ContactPoint',
        telephone: brandInfo.contact.phone,
        email: brandInfo.contact.email,
        contactType: 'customer service',
        availableLanguage: 'Chinese',
      },
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.douhuavideo.com/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: '关于我们',
      },
    ],
  };

  return (
    <>
      <SchemaOrg type="WebPage" data={aboutSchema} />
      <SchemaOrg type="BreadcrumbList" data={breadcrumbSchema} />
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 md:pt-24">
          <div className="w-full px-4 md:px-6 max-w-5xl mx-auto">
            <div className="mb-6">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-8">
              {t('about.title')}
            </h1>

            <BrandIntro />
            <CoreValues />
            <ReputationSection />
            <ContactSection />
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}